import React, { useState } from 'react'
import { Popover, SvgIcon } from '@mui/material'
import { format, isAfter, startOfDay } from 'date-fns'
import PropTypes from 'prop-types'
import { t } from 'i18next'

import TextField from '@hapana/shared/web/components/shared/text-field/text-field.component'
import CalendarIcon from '@hapana/shared/web/styles/assets/svgs/schedule-small.svg'
import DatePicker from '@hapana/shared/web/components/shared/date-picker/date-picker.component'
import { VARIANTS } from '@hapana/shared/web/components/shared/date-picker/date-picker.constants'
import { commonStyles } from '../../../../styles/common.styles'
import { styles } from './date-range-picker.styles'
import useAnchor from '../../../../hooks/usePopover'
import { getTodayInTimezone } from '@hapana/shared/utils/date.util'

export const START_DATE = 'startDate'
export const END_DATE = 'endDate'

const CustomDateRangePicker = ({
  initialStartDate,
  initialEndDate,
  picker,
  onDateChange,
  maxDate,
  displayFormat,
  layout,
  timezone,
}) => {
  const todayInSiteTimezone = timezone
    ? new Date(getTodayInTimezone(timezone) + 'T00:00:00')
    : new Date()

  const [startDate, setStartDate] = useState(initialStartDate || todayInSiteTimezone)
  const [endDate, setEndDate] = useState(initialEndDate || todayInSiteTimezone)
  const {
    anchorEl: startAnchorEl,
    open: startOpen,
    handleOpen: handleStartOpen,
    handleClose: handleStartClose,
  } = useAnchor()
  const {
    anchorEl: endAnchorEl,
    open: endOpen,
    handleOpen: handleEndOpen,
    handleClose: handleEndClose,
  } = useAnchor()

  const handleDateChange = (newValue, field) => {
    if (field === START_DATE) {
      setStartDate(newValue)
      onDateChange(START_DATE, newValue)
      if (isAfter(startOfDay(newValue), startOfDay(endDate))) {
        setEndDate(newValue)
        onDateChange(END_DATE, newValue)
      }
      if (
        newValue.getDate() !== startDate.getDate() &&
        newValue.getFullYear() === startDate.getFullYear() &&
        newValue.getMonth() === startDate.getMonth()
      ) {
        handleStartClose()
      }
    } else if (field === END_DATE) {
      setEndDate(newValue)
      onDateChange(END_DATE, newValue)
      if (
        newValue.getDate() !== endDate.getDate() &&
        newValue.getFullYear() === endDate.getFullYear() &&
        newValue.getMonth() === endDate.getMonth()
      ) {
        handleEndClose()
      }
    }
  }

  return (
    <>
      <TextField
        value={startDate ? (displayFormat ? format(startDate, displayFormat) : startDate) : ''}
        id={START_DATE}
        label={t('common:startDate')}
        name={START_DATE}
        sx={{ ...commonStyles.fullWidth, cursor: 'pointer' }}
        InputLabelProps={{ shrink: !!startDate }}
        inputProps={{ readOnly: true }}
        onClick={handleStartOpen}
        endAdornment={<SvgIcon component={CalendarIcon} onClick={handleStartOpen} />}
      />
      <Popover
        open={startOpen}
        anchorEl={startAnchorEl}
        onClose={handleStartClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <DatePicker
          picker={picker}
          value={startDate}
          onChange={(newValue) => handleDateChange(newValue, START_DATE)}
          name={START_DATE}
          variant={VARIANTS.DAY}
          timezone={timezone}
        />
      </Popover>
      <TextField
        value={displayFormat && endDate ? format(endDate, displayFormat) : endDate ? endDate : ''}
        id={END_DATE}
        label={t('common:endDate')}
        name={END_DATE}
        sx={layout === 'horizontal' ? styles.endDateFieldHorizontal : styles.endDateField}
        InputLabelProps={{ shrink: !!endDate }}
        inputProps={{ readOnly: true }}
        onClick={handleEndOpen}
        endAdornment={<SvgIcon component={CalendarIcon} onClick={handleEndOpen} />}
      />
      <Popover
        open={endOpen}
        anchorEl={endAnchorEl}
        onClose={handleEndClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <DatePicker
          picker={picker}
          value={endDate}
          minDate={startDate}
          onChange={(newValue) => handleDateChange(newValue, END_DATE)}
          name={END_DATE}
          variant={VARIANTS.DAY}
          maxDate={maxDate}
          timezone={timezone}
        />
      </Popover>
    </>
  )
}

CustomDateRangePicker.propTypes = {
  initialStartDate: PropTypes.any.isRequired,
  initialEndDate: PropTypes.any.isRequired,
  picker: PropTypes.string,
  onDateChange: PropTypes.func.isRequired,
  maxDate: PropTypes.any,
  displayFormat: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  timezone: PropTypes.string,
}

CustomDateRangePicker.defaultProps = {
  picker: null,
}

export default CustomDateRangePicker
